import React, { useEffect, useState, useMemo } from "react";
import styles from "../FnbReporting/index.module.css";
import myStyles from "./index.module.css";
import Report from "../../elements/Report";
import AddReportProduct from "../AddReportProduct";
import EditReportProduct from "../EditReportProduct";
import DropDown from "../../elements/Dropdown";
import PdfDocumentDutyFree from "../../layouts/PdfDocumentDutyFree";

import {
  openEdit,
  openPopup,
} from "../../../store/slices/addReport/popup.slice";
import { useSelector, useDispatch } from "react-redux";
import { removeReport } from "../../../store/slices/addReport/productYourReport.slice";
import Header from "../../elements/Header";
import { locationMapping, productData, Source } from "../../../utils/constant";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import {
  deleteReportAsyncThunk,
  getReportAsyncThunk,
} from "../../../store/slices/addReportDutyFree/productYourReport.slice";
import { productAxios } from "../../../utils/axios/product.axios";
import PdfDocument from "../../layouts/PdfDocument";
import { convert_usd_to_nzd } from "../../../utils/conversion";
import PulseLoader from "react-spinners/PulseLoader";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Reporting = () => {
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState(0); // 0 for A-Z, 1 for Z-A
  const sortOptions = ["A-Z", "Z-A"];
  const addReportState = useSelector((store) => store.addReportDutyFree); // Hide and show form.
  const yourReportsState = useSelector(
    (store) => store.productYourReportsDutyFree
  ); // List of your reports
  const productYourReportState = useSelector(
    (store) => store.productYourReportsDutyFree
  );
  const { source, currency } = useSelector((store) => store.constant);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getReportAsyncThunk());
  }, []);

  const handleSortChange = (value, index) => {
    // Dropdown onChange provides (value, index)
    setSortOrder(index);
  };

  // Memoize filtered and sorted reports to ensure proper re-sorting
  const filteredAndSortedReports = useMemo(() => {
    if (!yourReportsState.productYourReports || yourReportsState.productYourReports.length === 0) {
      return [];
    }
    const filtered = yourReportsState.productYourReports.filter((i) => 
      i.locations && i.locations.includes(source)
    );
    const reports = [...filtered];
    return reports.sort((a, b) => {
      const nameA = (a.name || "").toUpperCase();
      const nameB = (b.name || "").toUpperCase();
      if (sortOrder === 0) {
        // A-Z: ascending
        return nameA.localeCompare(nameB);
      } else {
        // Z-A: descending
        return nameB.localeCompare(nameA);
      }
    });
  }, [yourReportsState.productYourReports, source, sortOrder]);

  // Show form for add report.
  const showAddReport = () => dispatch(openPopup());

  // Show edit report form.
  const showEditReport = (index) =>
    dispatch(openEdit({ show: true, reportIndex: index }));

  // Delete report.
  const deleteReport = (index, id) => {
    dispatch(deleteReportAsyncThunk({ index, id }));
  };

  const formatDataForPDFDownload = (data) => {
    const subcategoryGroups = {};
    
    data.data.forEach((item) => {
      const sourceProduct = item.products_data.find(product => product.website === source);
      const subcategory = sourceProduct?.sub_category || 'uncategorized';
      
      if (!subcategoryGroups[subcategory]) {
        subcategoryGroups[subcategory] = [];
      }
      subcategoryGroups[subcategory].push(item);
    });

    const getItems = (subcategory) => {
      const products = subcategoryGroups[subcategory] || [];
      let items = [];

      products.forEach((product, index) => {
        const sourceProduct = product.products_data.find(
          (item) => item.website === source
        );
        
        if (!sourceProduct) {
          return; // Skip this product if no source product found
        }
        let compareProductsData = [];
        let localPriceSumOfCompareProducts = 0;
        const sourceLocalPrice = parseFloat(
          convert_usd_to_nzd(
            sourceProduct.latest_price,
            currency,
            sourceProduct
          )
        );

        product.products_data.forEach((item) => {
          const itemPromos = (item.latest_promotions || []).filter(
            (p) => p && (p.text || p.price != null)
          );
          const itemPromoLabels = itemPromos
            .map((p) =>
              (p.text && p.text.trim()) ||
              (p.price != null ? `$${Number(p.price).toFixed(2)}` : "Promo")
            )
            .filter(Boolean);
          const itemPromoUniqueLabels = [...new Set(itemPromoLabels)];
          const itemOnPromo = itemPromoUniqueLabels.length
            ? itemPromoUniqueLabels.join(", ")
            : "-";

          // Match DfProductCard: convert each price with its own rates, then subtract
          const itemLocalPrice = parseFloat(
            convert_usd_to_nzd(item.latest_price, currency, item)
          );
          const compareDifference =
            !Number.isNaN(sourceLocalPrice) && !Number.isNaN(itemLocalPrice)
              ? sourceLocalPrice - itemLocalPrice
              : NaN;

          let compareProduct = {
            to:
              locationMapping[item.website].location +
              "-" +
              locationMapping[item.website].brand,
            item: item.title,
            price: Number.isNaN(itemLocalPrice)
              ? "-"
              : "$" + itemLocalPrice.toFixed(2),
            difference: Number.isNaN(compareDifference)
              ? "-"
              : `${compareDifference >= 0 ? "$" : "-$"}${Math.abs(
                  compareDifference
                ).toFixed(2)}`,
            onPromo: itemOnPromo,
            unit: item.unit,
            qty: item.qty,
          };
          compareProductsData.push(compareProduct);
          // Only add to sum if it's not the source product
          if (item.website !== source && !Number.isNaN(itemLocalPrice)) {
            localPriceSumOfCompareProducts += itemLocalPrice;
          }
        });

        // Use API values if available, otherwise calculate
        const apiDifference = product.difference;
        const apiPercentage = product.difference_percentage;
        
        // Calculate comparable price from converted competitor prices (same as DfProductCard)
        const comparisonProducts = product.products_data.filter(item => item.website !== source);
        let comparablePrice =
          comparisonProducts.length > 0
            ? localPriceSumOfCompareProducts / comparisonProducts.length
            : 0;
        
        // Prefer local converted prices for Diff $ (same as DfProductCard)
        let difference, percentage;
        if (comparisonProducts.length > 0 && !Number.isNaN(sourceLocalPrice)) {
          difference = sourceLocalPrice - comparablePrice;
          percentage =
            sourceLocalPrice !== 0
              ? (difference / sourceLocalPrice) * 100
              : 0;
        } else if (apiDifference && apiPercentage) {
          difference = parseFloat(
            convert_usd_to_nzd(Number(apiDifference), currency, sourceProduct)
          );
          percentage = parseFloat(apiPercentage);
        } else {
          difference = 0;
          percentage = 0;
        }

        // Color coding logic for duty-free reports: 0 or negative = green, 0-15 = yellow, >15 = red
        let category;
        if (percentage <= 0) {
          category = "positive"; // Green for 0 or negative percentages (competitive)
        } else if (percentage > 0 && percentage <= 15) {
          category = "watch"; // Yellow for 0-15% (moderately expensive)
        } else {
          category = "negative"; // Red for > 15% (very expensive)
        }

        const priceRankVsComparative = sourceProduct.pricerank || "-";
        const promos = (sourceProduct.latest_promotions || []).filter((p) => p && (p.text || p.price != null));
        const labels = promos.map((p) => (p.text && p.text.trim()) || (p.price != null ? `$${Number(p.price).toFixed(2)}` : "Promo")).filter(Boolean);
        const uniqueLabels = [...new Set(labels)];
        const onPromo = uniqueLabels.length ? uniqueLabels.join(", ") : "-";
        let avg90DayChangeInPrice = "-";
        if (sourceProduct.avg_90day_price_change != null && !isNaN(Number(sourceProduct.avg_90day_price_change))) {
          const v = convert_usd_to_nzd(Number(sourceProduct.avg_90day_price_change), currency, sourceProduct);
          avg90DayChangeInPrice = (v >= 0 ? "$" : "-$") + Math.abs(v).toFixed(2);
        }

        let temp = {
          itemNo: index + 1,
          itemType: locationMapping[sourceProduct.website].type,
          subcategory: sourceProduct.sub_category || 'N/A',
          sku: sourceProduct.sku ?? "",
          description: sourceProduct.title,
          price: Number.isNaN(sourceLocalPrice)
            ? "-"
            : "$" + sourceLocalPrice.toFixed(2),
          compare: compareProductsData,
          comparablePrice: `${comparablePrice >= 0 ? "$" : "-$"}${Math.abs(
            comparablePrice
          ).toFixed(2)}`,
          difference: `${difference >= 0 ? "$" : "-$"}${Math.abs(difference).toFixed(2)}`,
          percentage: Number(percentage).toFixed(2) + "%",
          category: category,
          brand: sourceProduct.brand || 'N/A',
          productCategory: sourceProduct.category || 'N/A',
          priceRankVsComparative,
          onPromo,
          avg90DayChangeInPrice,
        };

        items.push(temp);
      });

      return items;
    };

    // Create formatted data for each subcategory
    const formattedData = Object.keys(subcategoryGroups).map(subcategory => ({
      name: subcategory.charAt(0).toUpperCase() + subcategory.slice(1).replace(/_/g, ' '),
      location: locationMapping[source].location,
      items: getItems(subcategory),
    }));

    // Sort subcategories alphabetically
    formattedData.sort((a, b) => a.name.localeCompare(b.name));

    return formattedData;
  };

  // Helper to fetch all report data in chunks
  const fetchAllReportChunks = async (params) => {
    const limit = 500;
    let offset = 0;
    let allData = [];
    let totals = 0;
    let firstResponse;

    // First call
    try {
      firstResponse = await productAxios.get("", {
        params: { ...params, limit, offset },
      });
    } catch (err) {
      return { status: "fail", error: err };
    }

    allData = firstResponse.data || [];
    totals = firstResponse.totals || allData.length;

    // If more data, fetch in chunks
    const calls = [];
    for (let nextOffset = limit; nextOffset < totals; nextOffset += limit) {
      calls.push(
        productAxios.get("", {
          params: { ...params, limit, offset: nextOffset },
        })
      );
    }

    if (calls.length > 0) {
      try {
        const results = await Promise.all(calls);
        results.forEach((res) => {
          allData = allData.concat(res.data || []);
        });
      } catch (err) {
        return { status: "fail", error: err };
      }
    }

    // Return a merged response object
    return { ...firstResponse, data: allData, totals };
  };

  const prepareReportDataFor = async (index, type) => {
    let reportData, apiParams;

    if (type === "custom")
      reportData = yourReportsState?.productYourReports[index];
    else reportData = productYourReportState?.productYourReports[index];

    if (!reportData) return {};

    // Ensure source location is always included in the API request
    let locations = reportData?.locations;
    if (locations && !locations.includes(source)) {
      locations = locations + "," + source;
    }

    apiParams = {
      source,
      category: reportData?.categories,
      sub_category: reportData?.subcategories && reportData?.subcategories.trim() !== "" 
        ? reportData?.subcategories 
        : undefined,
      brand: reportData?.brands,
      location: locations,
      canprod_ids:reportData?.canprod_ids
    };


    // Use the chunked fetcher
    const mergedData = await fetchAllReportChunks(apiParams);

    if (mergedData.status === "fail") {
      return mergedData;
    }

    return formatDataForPDFDownload(mergedData);
  };

  const prepareSystemReportDataFor = async (e) => {
    const idx = e?.currentTarget?.dataset?.index;

    if (!idx) return;

    let data = { data: [] };

    // Get user's accessible locations
    const userData = JSON.parse(localStorage.getItem("data"));
    const userAccessibleLocations = userData.user_source || [];

    // Define NZ domestic locations that should always be included for NZ sources
    const nzDomesticLocations = [
      'sephora', 'beauty_bliss', 'farmers', 'nz_liquor', 
      'big_barrel', 'chemist_warehouse', 'whisky_and_more', 'mecca'
    ];

    // Define Australian domestic locations that should always be included for Australian sources
    const auDomesticLocations = [
      'au_chemist_warehouse', 'au_mecca', 'au_sephora', 'the_iconic', 'danmurphy'
    ];

    // Define all duty-free locations that should always be included for airport comparisons
    const allDutyFreeLocations = [
      'aelia_auckland', 'aelia_queenstown', 'aelia_christchurch',
      'lotte_brisbane', 'lotte_melbourne', 'heinemann_sydney', 
      'aelia_adelaide', 'aelia_cairns', 'heinemann_goldcoast'
    ];

    // Check if current source is a NZ source
    const isNZSource = ['aelia_auckland', 'aelia_queenstown', 'aelia_christchurch'].includes(source);
    
    // Check if current source is an Australian source
    const isAUSource = ['heinemann_sydney', 'heinemann_goldcoast', 'lotte_melbourne', 'lotte_brisbane', 'aelia_cairns', 'aelia_adelaide'].includes(source);

    if (idx == 0) {
      let domesticLocations;
      if (isNZSource) {
        // For NZ sources, include all NZ domestic locations
        domesticLocations = nzDomesticLocations;
      } else if (isAUSource) {
        // For Australian sources, include all Australian domestic locations
        domesticLocations = auDomesticLocations;
      } else {
        // For other sources, filter based on user access
        domesticLocations = Object.keys(locationMapping)
        .filter((item) => 
          locationMapping[item].type === "Domestic" && 
          userAccessibleLocations.includes(item)
        );
      }
      
      data = await productAxios.get("", {
        params: {
          source,
          pricerange: "expensive",
          location: domesticLocations.join(",") + "," + source,
          sort: "difference_percentage_high_to_low",
          limit: 100,
          offset: 0,
        },
      });
    } else if (idx == 1) {
      let domesticLocations;
      if (isNZSource) {
        // For NZ sources, include all NZ domestic locations
        domesticLocations = nzDomesticLocations;
      } else if (isAUSource) {
        // For Australian sources, include all Australian domestic locations
        domesticLocations = auDomesticLocations;
      } else {
        // For other sources, filter based on user access
        domesticLocations = Object.keys(locationMapping)
        .filter((item) => 
          locationMapping[item].type === "Domestic" && 
          userAccessibleLocations.includes(item)
        );
      }
      
      data = await productAxios.get("", {
        params: {
          source,
          pricerange: "cheapest",
          location: domesticLocations.join(",") + "," + source,
          sort: "difference_percentage_low_to_high",
          limit: 100,
          offset: 0,
        },
      });
    } else if (idx == 2) {
      // For airport comparisons, always include all duty-free locations
      const dutyFreeLocations = allDutyFreeLocations.filter(location => location !== source);
      
      data = await productAxios.get("", {
        params: {
          source,
          pricerange: "expensive",
          location: dutyFreeLocations.join(",") + "," + source,
          sort: "difference_percentage_high_to_low",
          limit: 100,
          offset: 0,
        },
      });
    } else if (idx == 3) {
      // For airport comparisons, always include all duty-free locations
      const dutyFreeLocations = allDutyFreeLocations.filter(location => location !== source);
      
      data = await productAxios.get("", {
        params: {
          source,
          pricerange: "cheapest",
          location: dutyFreeLocations.join(",") + "," + source,
          sort: "difference_percentage_low_to_high",
          limit: 100,
          offset: 0,
        },
      });
    } else if (idx == 4) {
      data = await productAxios.get(`/marginally-behind?source=${source}`);
    }

    return formatDataForPDFDownload(data);
  };

  const CHUNK_SIZE = 500;

  const splitArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  const handleDownload = async (e) => {
    setLoading(true);
    const title = e?.currentTarget?.dataset?.title;
    const data = await prepareReportDataFor(
      e?.currentTarget?.dataset?.index,
      e?.currentTarget?.dataset?.type
    );

    if (data.status === "fail") {
      setLoading(false);
      console.log('1')
      toast.error("Something went wrong while download!");
      return;
    }

    // Process all subcategories
    const allSubcategories = data || [];
    const maxItemsPerChunk = CHUNK_SIZE;
    const totalItems = allSubcategories.reduce((sum, subcat) => sum + subcat.items.length, 0);
    
    if (totalItems === 0) {
      setLoading(false);
      toast.error("No data to export.");
      return;
    }

    // Create chunks of subcategories
    const chunks = [];
    let currentChunk = [];
    let currentItemCount = 0;

    allSubcategories.forEach(subcategory => {
      if (currentItemCount + subcategory.items.length <= maxItemsPerChunk) {
        currentChunk.push(subcategory);
        currentItemCount += subcategory.items.length;
      } else {
        if (currentChunk.length > 0) {
          chunks.push([...currentChunk]);
        }
        currentChunk = [subcategory];
        currentItemCount = subcategory.items.length;
      }
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    for (let i = 0; i < chunks.length; i++) {
      const chunkedData = chunks[i];

      const doc = <PdfDocument data={chunkedData} section="df" />;
      try {
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}_part${i + 1}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        setLoading(false);
        toast.error("Failed to generate PDF.");
        return;
      }
    }

    setLoading(false);
    if (chunks.length > 1) {
      toast.info(`Report was split into ${chunks.length} files due to size.`);
    }
  };

  const handleDownloadSystem = async (e) => {
    setLoading(true);
    const title = e.currentTarget.dataset.title;
    const data = await prepareSystemReportDataFor(e);

    const doc = <PdfDocument data={data} section={"df"} />; // Your PDF document component

    // Generate the PDF blob
    const blob = await pdf(doc).toBlob();

    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.download = title; // Specify the file name
    link.click(); // Trigger the download
    setLoading(false);
    // Clean up by revoking the URL after download
    URL.revokeObjectURL(url);
  };

  // Helper function to get source website name dynamically (location only, e.g. "Auckland")
  const getSourceWebsiteName = () => {
    const sourceInfo = locationMapping[source];
    if (sourceInfo) {
      return sourceInfo.location || "Unknown Source";
    }
    return "Unknown Source";
  };

  // Helper function to get normalized source name for filtering
  const getNormalizedSourceName = () => {
    const sourceInfo = locationMapping[source];
    if (sourceInfo) {
      return `${sourceInfo.location}-${sourceInfo.brand}`;
    }
    return "";
  };

  const handleDownloadExcel = async (e) => {
    setLoading(true);
    const title = e?.currentTarget?.dataset?.title;
    const data = await prepareReportDataFor(
      e?.currentTarget?.dataset?.index,
      e?.currentTarget?.dataset?.type
    );

    if (data.status === "fail") {
      setLoading(false);
      console.log('2')
      toast.error("Something went wrong while download!");
      return;
    }

    // Combine all items from all subcategories
    const allItems = data.reduce((acc, subcategory) => {
      return acc.concat(subcategory.items || []);
    }, []);
    
    if (allItems.length === 0) {
      setLoading(false);
      toast.error("No data to export.");
      return;
    }

    // Helper to normalize website labels for comparison
    const normalize = (str) =>
      (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const SOURCE_WEBSITE = source;
    const normalizedSource = normalize(SOURCE_WEBSITE);
    const allWebsites = new Set();
    allItems.forEach((item) => {
      (item.compare || []).forEach((cmp) => {
        if (cmp.to && normalize(cmp.to) !== normalizedSource)
          allWebsites.add(cmp.to);
      });
    });
    const websiteList = Array.from(allWebsites)?.filter(
      (item) => item !== getNormalizedSourceName()
    );

    // Map websites to tags (duty-free/domestic)
    const websiteTagMap = {};
    Object.keys(locationMapping).forEach((key) => {
      websiteTagMap[
        locationMapping[key].location + "-" + locationMapping[key].brand
      ] = locationMapping[key].type?.toLowerCase();
    });

    // Group websites
    const dutyFreeWebsites = websiteList.filter((site) =>
      (websiteTagMap[site] || "").includes("duty")
    );
    const domesticWebsites = websiteList.filter((site) =>
      (websiteTagMap[site] || "").includes("domestic")
    );

    // --- Build multi-row header ---
    // We now have 8 source columns (no subcategory column in Excel)
    const sourceCols = 8;
    const perSiteCols = 5;
    const sourceWebsiteName = getSourceWebsiteName();
    const row1 = [
      ...Array(sourceCols).fill(""),
      ...Array(dutyFreeWebsites.length * perSiteCols).fill(""),
      ...Array(domesticWebsites.length * perSiteCols).fill(""),
    ];
    if (dutyFreeWebsites.length > 0) row1[sourceCols] = "Duty Free";
    if (domesticWebsites.length > 0)
      row1[sourceCols + dutyFreeWebsites.length * perSiteCols] = "Domestic";
    const row2 = [
      sourceWebsiteName,
      ...Array(sourceCols - 1).fill(""),
      ...dutyFreeWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
      ...domesticWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
    ];
    const row3 = [
      "SKU",
      `${sourceWebsiteName} title`,
      `${sourceWebsiteName} brand`,
      `${sourceWebsiteName} category`,
      "Price Rank vs Comparative Retailer",
      "On promo",
      "Avg 90 days change in price ($)",
      `${sourceWebsiteName} price`,
      ...[...dutyFreeWebsites, ...domesticWebsites].flatMap(() => [
        "Price",
        "Diff %",
        "Diff $",
        "On promo",
        "Volume",
      ]),
    ];

    const rows = allItems.map((item) => {
      const row = [
        item.sku ?? "-",
        item.description || "-",
        item.brand || "-",
        item.productCategory || "-",
        item.priceRankVsComparative ?? "-",
        item.onPromo ?? "-",
        item.avg90DayChangeInPrice ?? "-",
        item.price || "-",
      ];
      [...dutyFreeWebsites, ...domesticWebsites].forEach((site) => {
        const cmp = (item.compare || []).find((c) => c.to === site);
        row.push(cmp ? cmp.price : "-");
        let sourcePrice = parseFloat(
          (item.price || "").replace(/[^\d.\-]/g, "")
        );
        let websitePrice = cmp
          ? parseFloat((cmp.price || "").replace(/[^\d.\-]/g, ""))
          : NaN;
        if (
          cmp &&
          !isNaN(sourcePrice) &&
          !isNaN(websitePrice) &&
          websitePrice !== 0
        ) {
          let diff = ((sourcePrice - websitePrice) / websitePrice) * 100;
          row.push(diff.toFixed(2) + "%");
        } else {
          row.push("-");
        }
        row.push(cmp && cmp.difference != null ? cmp.difference : "-");
        row.push(cmp && cmp.onPromo ? cmp.onPromo : "-");
        const volume = cmp?.qty && cmp?.unit ? cmp?.qty + " " + cmp?.unit : "-";
        row.push(volume);
      });
      return row;
    });

    // Build worksheet data
    const ws_data = [row1, row2, row3, ...rows];

    const merges = [];
    if (dutyFreeWebsites.length > 0) {
      merges.push({
        s: { r: 0, c: sourceCols },
        e: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols - 1 },
      });
    }
    if (domesticWebsites.length > 0) {
      merges.push({
        s: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols },
        e: {
          r: 0,
          c: sourceCols + dutyFreeWebsites.length * perSiteCols + domesticWebsites.length * perSiteCols - 1,
        },
      });
    }
    [...dutyFreeWebsites, ...domesticWebsites].forEach((site, idx) => {
      const col = sourceCols + idx * perSiteCols;
      merges.push({ s: { r: 1, c: col }, e: { r: 1, c: col + perSiteCols - 1 } });
    });

    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${title}.xlsx`
    );
    setLoading(false);
    toast.success("Excel downloaded!");
  };

  const handleDownloadExcelSystem = async (e) => {
    setLoading(true);
    const title = e?.currentTarget?.dataset?.title;
    const data = await prepareSystemReportDataFor(e);

    if (data.status === "fail") {
      setLoading(false);
      console.log('3')
      toast.error("Something went wrong while download!");
      return;
    }

    // Combine all items from all subcategories
    const allItems = data.reduce((acc, subcategory) => {
      return acc.concat(subcategory.items || []);
    }, []);
    
    if (allItems.length === 0) {
      setLoading(false);
      toast.error("No data to export.");
      return;
    }

    // Helper to normalize website labels for comparison
    const normalize = (str) =>
      (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const SOURCE_WEBSITE = source;
    const normalizedSource = normalize(SOURCE_WEBSITE);
    const allWebsites = new Set();
    allItems.forEach((item) => {
      (item.compare || []).forEach((cmp) => {
        if (cmp.to && normalize(cmp.to) !== normalizedSource)
          allWebsites.add(cmp.to);
      });
    });
    const websiteList = Array.from(allWebsites)?.filter(
      (item) => item !== getNormalizedSourceName()
    );

    // Map websites to tags (duty-free/domestic)
    const websiteTagMap = {};
    Object.keys(locationMapping).forEach((key) => {
      websiteTagMap[
        locationMapping[key].location + "-" + locationMapping[key].brand
      ] = locationMapping[key].type?.toLowerCase();
    });

    // Group websites
    const dutyFreeWebsites = websiteList.filter((site) =>
      (websiteTagMap[site] || "").includes("duty")
    );
    const domesticWebsites = websiteList.filter((site) =>
      (websiteTagMap[site] || "").includes("domestic")
    );

    // --- Build multi-row header ---
    // We now have 8 source columns (no subcategory column in Excel)
    const sourceCols = 8;
    const perSiteCols = 5;
    const sourceWebsiteName = getSourceWebsiteName();
    const row1 = [
      ...Array(sourceCols).fill(""),
      ...Array(dutyFreeWebsites.length * perSiteCols).fill(""),
      ...Array(domesticWebsites.length * perSiteCols).fill(""),
    ];
    if (dutyFreeWebsites.length > 0) row1[sourceCols] = "Duty Free";
    if (domesticWebsites.length > 0)
      row1[sourceCols + dutyFreeWebsites.length * perSiteCols] = "Domestic";
    const row2 = [
      sourceWebsiteName,
      ...Array(sourceCols - 1).fill(""),
      ...dutyFreeWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
      ...domesticWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
    ];
    const row3 = [
      "SKU",
      `${sourceWebsiteName} title`,
      `${sourceWebsiteName} brand`,
      `${sourceWebsiteName} category`,
      "Price Rank vs Comparative Retailer",
      "On promo",
      "Avg 90 days change in price ($)",
      `${sourceWebsiteName} price`,
      ...[...dutyFreeWebsites, ...domesticWebsites].flatMap(() => [
        "Price",
        "Diff %",
        "Diff $",
        "On promo",
        "Volume",
      ]),
    ];

    const rows = allItems.map((item) => {
      const row = [
        item.sku ?? "-",
        item.description || "-",
        item.brand || "-",
        item.productCategory || "-",
        item.priceRankVsComparative ?? "-",
        item.onPromo ?? "-",
        item.avg90DayChangeInPrice ?? "-",
        item.price || "-",
      ];
      [...dutyFreeWebsites, ...domesticWebsites].forEach((site) => {
        const cmp = (item.compare || []).find((c) => c.to === site);
        row.push(cmp ? cmp.price : "-");
        let sourcePrice = parseFloat(
          (item.price || "").replace(/[^\d.\-]/g, "")
        );
        let websitePrice = cmp
          ? parseFloat((cmp.price || "").replace(/[^\d.\-]/g, ""))
          : NaN;
        if (
          cmp &&
          !isNaN(sourcePrice) &&
          !isNaN(websitePrice) &&
          websitePrice !== 0
        ) {
          let diff = ((sourcePrice - websitePrice) / websitePrice) * 100;
          row.push(diff.toFixed(2) + "%");
        } else {
          row.push("-");
        }
        row.push(cmp && cmp.difference != null ? cmp.difference : "-");
        row.push(cmp && cmp.onPromo ? cmp.onPromo : "-");
        const volume = cmp?.qty && cmp?.unit ? cmp?.qty + " " + cmp?.unit : "-";
        row.push(volume);
      });
      return row;
    });

    // Build worksheet data
    const ws_data = [row1, row2, row3, ...rows];

    const merges = [];
    if (dutyFreeWebsites.length > 0) {
      merges.push({
        s: { r: 0, c: sourceCols },
        e: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols - 1 },
      });
    }
    if (domesticWebsites.length > 0) {
      merges.push({
        s: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols },
        e: {
          r: 0,
          c: sourceCols + dutyFreeWebsites.length * perSiteCols + domesticWebsites.length * perSiteCols - 1,
        },
      });
    }
    [...dutyFreeWebsites, ...domesticWebsites].forEach((site, idx) => {
      const col = sourceCols + idx * perSiteCols;
      merges.push({ s: { r: 1, c: col }, e: { r: 1, c: col + perSiteCols - 1 } });
    });

    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${title}.xlsx`
    );
    setLoading(false);
    toast.success("Excel downloaded!");
  };

  return (
    <>
      <div className={styles["container"]}>
        {/* Heading */}
        <Header />
        <hr />

        {/* System reports */}
        <div className={styles["system"]}>
          <div className={styles["heading"]}>
            <h3>System Reports</h3>
            <p>
              Our pre-made reports are tailored to help you take action and
              become competitive in the most efficient way.
            </p>
          </div>

          <div className={styles["reports"]}>
            <Report
              onDownload={handleDownloadSystem}
              onDownloadExcel={handleDownloadExcelSystem}
              index={0}
              title={
                "List of the 100 least competitive products compared to the domestic market"
              }
            >
              List of the 100 least competitive products compared to the domestic market
            </Report>
            <Report
              onDownload={handleDownloadSystem}
              onDownloadExcel={handleDownloadExcelSystem}
              index={1}
              title={
                "List of the 100 most competitive products compared to the domestic market"
              }
            >
              List of the 100 most competitive products compared to the domestic
              market.
            </Report>
            <Report
              onDownload={handleDownloadSystem}
              onDownloadExcel={handleDownloadExcelSystem}
              index={2}
              title={
                "List of the 100 least competitive products compared to other airports"
              }
            >
              List of the 100 least competitive products compared to other
              airports.
            </Report>
            <Report
              onDownload={handleDownloadSystem}
              onDownloadExcel={handleDownloadExcelSystem}
              index={3}
              title={
                "List of the 100 most competitive products compared to other airports"
              }
            >
              List of the 100 most competitive products compared to other
              airports.
            </Report>
            {/* <Report onDownload={handleDownloadSystem} index={4}>
              List of products that are only marginally behind the next
              competitor.
            </Report> */}
          </div>
        </div>

        {/* Your reports */}
        <div className={styles["your"]}>
          <div className={styles["heading"]}>
            <div className={styles["titleAddReport"]}>
              <h3>Your Reports</h3>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                <div className={styles["sortDropdownWrapper"]}>
                  <DropDown
                    steps={sortOptions}
                    selectedValue={sortOrder}
                    onChange={handleSortChange}
                  />
                </div>
                <div className={styles["addReport"]} onClick={showAddReport}>
                  Add Report
                </div>
              </div>
            </div>
            <p>Generate custom reports as per your requirement.</p>
          </div>
          {yourReportsState.loading ? (
            <div className="loader-container">
              <PulseLoader />
            </div>
          ) : (
            <div className={styles["reports"]}>
              {filteredAndSortedReports.map((report, filteredIndex) => {
                  // Find the original index of this report in the unfiltered array
                  const originalIndex = yourReportsState.productYourReports.findIndex(
                    (originalReport) => originalReport.id === report.id
                  );
                  
                  return (
                    <Report
                      onDownload={handleDownload}
                      onDownloadExcel={handleDownloadExcel}
                      index={originalIndex}
                      type={"custom"}
                      title={report.name}
                      key={`${report.id}-${sortOrder}`}
                    >
                      {report.name}{" "}
                      <span>
                        <i
                          className="bi bi-pen"
                          onClick={() => showEditReport(originalIndex)}
                        ></i>
                        <i
                          className="bi bi-trash"
                          style={{ fontSize: "2.5rem" }}
                          onClick={() => deleteReport(originalIndex, report?.id)}
                        ></i>
                      </span>
                    </Report>
                  );
                })}
            </div>
          )}
        </div>

        {/* Add report */}
        {addReportState.showAddReport && <AddReportProduct />}

        {/* Edit report */}
        {addReportState.showEditReport.show && <EditReportProduct />}
      </div>
      {loading && (
        <div className={myStyles["loading-wrapper"]}>
          <PulseLoader />
        </div>
      )}
    </>
  );
};

export default Reporting;
